import { KeyboardEvent, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { Box, BoxProps } from 'reflexbox';
import styled, { css } from 'styled-components';

import { focusVisible } from '../helpers';
import { CheckmarkIcon, CloseIcon, DashIcon } from '../icons';
import { palettes, tokens } from '../theme/tokens';
import { Confetti } from './Confetti';

const DEFAULT_TODO_SIZE = 20;
const DEFAULT_ICON_SIZE = 14;

type TodoState = 'unchecked' | 'checked' | 'indeterminate' | 'late';

function iconSizeFor(size: number) {
	return Math.round((size / DEFAULT_TODO_SIZE) * DEFAULT_ICON_SIZE);
}

function resolveState(checked?: boolean | null, late?: boolean): TodoState {
	if (checked === null) {
		return 'indeterminate';
	}

	if (checked) {
		return 'checked';
	}

	if (late) {
		return 'late';
	}

	return 'unchecked';
}

const ContainerEnabled = css<{ $state: TodoState }>`
	cursor: pointer;

	${(props) =>
		props.$state === 'checked' &&
		`
		background: ${tokens.success.icon};
		border-color: ${palettes.freshGreen['600']};
		color: ${tokens.text.inverse};

		&:hover,
		&[data-preview='hover'] {
			background: ${palettes.freshGreen['600']};
			border-color: ${palettes.freshGreen['700']};
		}
	`}

	${(props) =>
		props.$state === 'late' &&
		`
		background: ${tokens.warning.icon};
		border-color: ${palettes.vibrantPeach['600']};
		color: ${tokens.text.inverse};

		&:hover,
		&[data-preview='hover'] {
			background: ${palettes.vibrantPeach['600']};
			border-color: ${palettes.vibrantPeach['700']};
		}
	`}

	${(props) =>
		props.$state === 'indeterminate' &&
		`
		background: ${tokens.text.primary};
		border-color: ${tokens.text.primary};
		color: ${tokens.text.inverse};

		&:hover,
		&[data-preview='hover'] {
			background: ${palettes.heatherGray['800']};
			border-color: ${palettes.heatherGray['800']};
		}
	`}

	${(props) =>
		props.$state === 'unchecked' &&
		`
		background: ${tokens.surface.white};
		border-color: ${tokens.border.strong};
		color: ${tokens.icon.primary};

		&:hover,
		&[data-preview='hover'] {
			border-color: ${tokens.border.focus};
		}
	`}

	${focusVisible}
`;

const ContainerReadOnly = css<{ $state: TodoState }>`
	${(props) =>
		props.$state === 'checked' &&
		`
		background: ${tokens.success.icon};
		border-color: ${palettes.freshGreen['600']};
		color: ${tokens.text.inverse};
	`}

	${(props) =>
		props.$state === 'late' &&
		`
		background: ${tokens.warning.icon};
		border-color: ${palettes.vibrantPeach['600']};
		color: ${tokens.text.inverse};
	`}

	${(props) =>
		props.$state === 'indeterminate' &&
		`
		background: ${tokens.text.primary};
		border-color: ${tokens.text.primary};
		color: ${tokens.text.inverse};
	`}

	${(props) =>
		props.$state === 'unchecked' &&
		`
		background: ${tokens.surface.white};
		border-color: ${tokens.border.strong};
		color: ${tokens.icon.primary};
	`}
`;

const Container = styled(Box)<{
	$state: TodoState;
	$interactive?: boolean;
	$size: number;
	$iconSize: number;
}>`
	width: ${(props) => props.$size}px;
	height: ${(props) => props.$size}px;
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	border: 1.25px solid ${tokens.border.strong};
	border-radius: ${tokens.radius.xs}px;
	flex-shrink: 0;

	${(props) => (props.$interactive ? ContainerEnabled : ContainerReadOnly)}

	svg {
		display: block;
		width: ${(props) => props.$iconSize}px;
		height: ${(props) => props.$iconSize}px;
		color: inherit;
	}
`;

export function Todo(
	props: BoxProps & {
		checked?: boolean | null;
		late?: boolean;
		size?: number;
		onChange?: (checked: boolean) => void;
		'data-preview'?: 'hover';
	},
) {
	const {
		checked,
		late,
		size = DEFAULT_TODO_SIZE,
		onChange,
		'data-preview': dataPreview,
		...rest
	} = props;
	const [confetti, setConfetti] = useState<{ x: number; y: number } | undefined>(undefined);
	const ref = useRef<HTMLElement>(null);
	const state = resolveState(checked, late);
	const iconSize = iconSizeFor(size);

	useUpdateEffect(() => {
		if (checked && ref.current) {
			const bounds = ref.current.getBoundingClientRect();

			setConfetti({
				x: (bounds.left + bounds.width / 2) / window.innerWidth,
				y: (bounds.top + bounds.height / 2) / window.innerHeight,
			});
		}
	}, [checked]);

	const icon =
		state === 'indeterminate' ? (
			<DashIcon size={iconSize} />
		) : state === 'checked' ? (
			<CheckmarkIcon size={iconSize} />
		) : state === 'late' ? (
			<CloseIcon size={iconSize} />
		) : null;

	const handleChange = () => {
		onChange?.(!checked);
	};

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (!onChange) {
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleChange();
		}
	};

	return (
		<>
			<Container
				{...rest}
				ref={ref}
				tabIndex={onChange ? 0 : -1}
				role="checkbox"
				aria-checked={checked === null ? 'mixed' : !!checked}
				$state={state}
				$interactive={!!onChange}
				$size={size}
				$iconSize={iconSize}
				data-preview={dataPreview}
				onClick={onChange ? handleChange : undefined}
				onKeyDown={onChange ? onKeyDown : undefined}
			>
				{icon}
			</Container>
			{confetti && <Confetti origin={confetti} onFinish={() => setConfetti(undefined)} />}
		</>
	);
}
